// src/api/axios.js
import axios from "axios";

/*
  SIMPLE + SAFE API BASE RESOLUTION

  Priority:
  1) REACT_APP_API_URL (Vercel / environment variable)
  2) If development → http://localhost:8000
  3) Production fallback → https://prospect-engage.onrender.com
*/

const isDev = process.env.NODE_ENV === "development";

export const API_BASE =
  process.env.REACT_APP_API_URL ||
  (isDev ? "http://localhost:8000" : "https://prospect-engage.onrender.com");

// ---- Storage key for bearer token ----
const STORAGE_KEY = "access_token";

// ---- In-memory token (faster + prevents race conditions) ----
let memoryToken = null;

// ---- Axios instance ----
const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  timeout: 30000,
});

// ---- Bootstrap guard ----
let bootstrapped = false;

export const markBootstrapped = () => {
  bootstrapped = true;
};

// Paths where 401 should NOT force logout
const EXEMPT_401_PATHS = [
  "/login",
  "/forgot-password",
  "/reset-password",
  "/auth/login",
  "/auth/refresh",
  "/auth/forgot-password",
  "/auth/reset-password",
  "/auth/invitations/",
];

/*
=====================================================
REQUEST INTERCEPTOR
Attach bearer token automatically
=====================================================
*/

api.interceptors.request.use(
  (config) => {
    try {
      let token = memoryToken;

      // fallback to storage if memory empty
      if (!token) {
        token = localStorage.getItem(STORAGE_KEY);
      }

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch {}

    if (config.data instanceof FormData) {
      delete config.headers?.["Content-Type"];
      delete config.headers?.["content-type"];
    } else if (!config.headers?.["Content-Type"] && !config.headers?.["content-type"]) {
      config.headers = config.headers || {};
      config.headers["Content-Type"] = "application/json";
    }

    return config;
  },
  (error) => Promise.reject(error),
);

/*
=====================================================
RESPONSE INTERCEPTOR
Handle global 401 errors
=====================================================
*/

api.interceptors.response.use(
  (res) => res,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config ? api.getUri(error.config) : "";

    if (status === 401 && bootstrapped) {
      const isExempt = EXEMPT_401_PATHS.some((p) => url.includes(p));

      if (!isExempt) {
        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch {}

        memoryToken = null;

        delete api.defaults.headers.common.Authorization;

        if (!window.location.pathname.startsWith("/login")) {
          window.location.assign("/login");
        }
      }
    }

    return Promise.reject(error);
  },
);

/*
=====================================================
AUTH TOKEN HELPERS
=====================================================
*/

export const setAuthToken = (token) => {
  if (token) {
    memoryToken = token;

    try {
      localStorage.setItem(STORAGE_KEY, token);
    } catch {}

    api.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    memoryToken = null;

    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {}

    delete api.defaults.headers.common.Authorization;
  }
};

/*
=====================================================
REQUEST CANCELLATION HELPER
=====================================================
*/

export const isCancel = (err) =>
  axios.isCancel?.(err) ||
  err?.name === "CanceledError" ||
  err?.code === "ERR_CANCELED";

export default api;
